
import React, { useState, useMemo } from 'react';
import { dbService } from '../services/dbService.ts';
import { ChatRequest, User, UserRole, SchedulingStatus } from '../types.ts';

const CalendarView = ({ user }: { user: User }) => {
  const [currentDate, setCurrentDate] = useState(new Date());
  const requests = dbService.getRequestsForUser(user.id, user.role);
  const scheduled = requests.filter(r => r.scheduledTime && r.schedulingStatus === SchedulingStatus.CONFIRMED);
  const allUsers = dbService.getUsers();

  const daysInMonth = new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 0).getDate();
  const firstDayOfMonth = new Date(currentDate.getFullYear(), currentDate.getMonth(), 1).getDay();

  const days = useMemo(() => {
    const arr = [];
    // Padding for start of month
    for (let i = 0; i < firstDayOfMonth; i++) arr.push(null);
    // Days of the month
    for (let i = 1; i <= daysInMonth; i++) arr.push(i);
    return arr;
  }, [currentDate, daysInMonth, firstDayOfMonth]);

  const getMeetingsForDay = (day: number) => {
    return scheduled.filter(req => {
      const d = new Date(req.scheduledTime!);
      return d.getDate() === day && d.getMonth() === currentDate.getMonth() && d.getFullYear() === currentDate.getFullYear();
    });
  };

  const monthName = currentDate.toLocaleString('default', { month: 'long' });

  return (
    <div className="p-8 lg:p-20 bg-slate-50/30 dark:bg-slate-950/30 min-h-screen transition-colors duration-500 font-sans overflow-y-auto">
        <header className="mb-12 flex flex-col md:flex-row justify-between items-start md:items-end gap-6">
          <div>
            <span className="text-[10px] font-black uppercase tracking-[0.4em] text-indigo-600 dark:text-indigo-400 mb-3 block">Neural Schedule</span>
            <h1 className="text-6xl font-black text-blue-950 dark:text-white tracking-tighter leading-none">{monthName} <span className="text-slate-300 dark:text-slate-700">{currentDate.getFullYear()}</span></h1>
          </div>
          <div className="flex gap-2 p-1 bg-white dark:bg-slate-900 rounded-2xl border border-slate-100 dark:border-slate-800 shadow-xl">
             <button onClick={() => setCurrentDate(new Date(currentDate.setMonth(currentDate.getMonth() - 1)))} className="w-12 h-12 flex items-center justify-center text-slate-400 hover:text-blue-950 dark:hover:text-white transition"><i className="fas fa-chevron-left"></i></button>
             <button onClick={() => setCurrentDate(new Date())} className="px-6 py-2 text-[10px] font-black uppercase tracking-widest text-blue-600 dark:text-indigo-400">Today</button>
             <button onClick={() => setCurrentDate(new Date(currentDate.setMonth(currentDate.getMonth() + 1)))} className="w-12 h-12 flex items-center justify-center text-slate-400 hover:text-blue-950 dark:hover:text-white transition"><i className="fas fa-chevron-right"></i></button>
          </div>
        </header>

        <div className="grid grid-cols-1 xl:grid-cols-4 gap-12">
           {/* Calendar Grid */}
           <div className="xl:col-span-3">
              <div className="bg-white dark:bg-slate-900 rounded-[3.5rem] border border-slate-100 dark:border-slate-800 shadow-4xl overflow-hidden p-8 lg:p-12">
                 <div className="grid grid-cols-7 gap-4 mb-8">
                    {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(d => (
                      <div key={d} className="text-center text-[10px] font-black uppercase tracking-[0.2em] text-slate-300 dark:text-slate-600">{d}</div>
                    ))}
                 </div>
                 <div className="grid grid-cols-7 gap-4">
                    {days.map((day, idx) => {
                      if (day === null) return <div key={`pad-${idx}`} className="aspect-square opacity-0"></div>;
                      
                      const meetings = getMeetingsForDay(day);
                      const isToday = day === new Date().getDate() && currentDate.getMonth() === new Date().getMonth();
                      
                      return (
                        <div 
                          key={day} 
                          className={`aspect-square relative rounded-3xl border transition-all group flex flex-col items-center justify-center ${
                            isToday 
                            ? 'bg-blue-50 dark:bg-indigo-900/20 border-blue-200 dark:border-indigo-500/30' 
                            : 'bg-white dark:bg-slate-800/50 border-slate-50 dark:border-slate-800 hover:border-slate-200 dark:hover:border-slate-700'
                          }`}
                        >
                          <span className={`text-lg font-black ${isToday ? 'text-blue-600 dark:text-indigo-400' : 'text-slate-400 dark:text-slate-500'} mb-1`}>{day}</span>
                          
                          {meetings.length > 0 && (
                            <div className="flex gap-1 justify-center mt-1">
                               {meetings.map((_, i) => (
                                 <div key={i} className="w-2 h-2 rounded-full bg-blue-600 dark:bg-indigo-500 shadow-lg shadow-blue-500/50 animate-pulse"></div>
                               ))}
                            </div>
                          )}

                          {/* Hover Details Popover */}
                          {meetings.length > 0 && (
                            <div className="absolute bottom-full mb-4 left-1/2 -translate-x-1/2 w-48 bg-blue-950 dark:bg-black text-white p-4 rounded-2xl shadow-4xl opacity-0 group-hover:opacity-100 pointer-events-none transition-all z-50 transform scale-95 group-hover:scale-100">
                               <p className="text-[10px] font-black uppercase tracking-widest text-indigo-400 mb-2">Protocol Active</p>
                               {meetings.map((m, i) => (
                                 <div key={i} className="mb-2 last:mb-0">
                                    <p className="text-xs font-bold truncate">{allUsers.find(u => u.id === m.studentId)?.fullName}</p>
                                    <p className="text-[9px] opacity-60 font-black">{new Date(m.scheduledTime!).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</p>
                                 </div>
                               ))}
                               <div className="absolute top-full left-1/2 -translate-x-1/2 border-8 border-transparent border-t-blue-950 dark:border-t-black"></div>
                            </div>
                          )}
                        </div>
                      );
                    })}
                 </div>
              </div>
           </div>

           {/* Sidebar: Upcoming Protocols */}
           <div className="space-y-10">
              <div>
                 <h3 className="text-2xl font-black text-blue-950 dark:text-white tracking-tighter mb-8 flex items-center gap-3">
                    <div className="w-2 h-8 bg-amber-400 rounded-full"></div> Protocol Log
                 </h3>
                 <div className="space-y-4">
                    {scheduled.length === 0 ? (
                      <div className="p-12 bg-white dark:bg-slate-900 rounded-[3rem] border border-dashed border-slate-100 dark:border-slate-800 text-center opacity-40">
                         <i className="fas fa-calendar-times text-3xl mb-4 text-slate-300"></i>
                         <p className="text-xs font-bold text-slate-400 uppercase tracking-widest">No active cycles</p>
                      </div>
                    ) : (
                      scheduled
                        .sort((a,b) => (a.scheduledTime || 0) - (b.scheduledTime || 0))
                        .slice(0, 5)
                        .map(req => {
                          const partner = allUsers.find(u => u.id === req.studentId);
                          const date = new Date(req.scheduledTime!);
                          return (
                            <div key={req.id} className="bg-white dark:bg-slate-900 p-6 rounded-[2.5rem] border border-slate-100 dark:border-slate-800 shadow-xl flex items-center gap-5 group hover:border-indigo-500/20 transition-all">
                               <div className="w-14 h-14 bg-indigo-50 dark:bg-indigo-900/20 text-indigo-600 dark:text-indigo-400 rounded-2xl flex flex-col items-center justify-center font-black shadow-inner shrink-0 group-hover:bg-indigo-600 group-hover:text-white transition-colors">
                                  <span className="text-xs uppercase leading-none mb-0.5">{date.toLocaleString('default', { month: 'short' })}</span>
                                  <span className="text-lg leading-none">{date.getDate()}</span>
                               </div>
                               <div className="min-w-0">
                                  <p className="font-black text-blue-950 dark:text-white text-base tracking-tight truncate">{partner?.fullName}</p>
                                  <p className="text-[10px] font-black text-slate-400 dark:text-slate-500 uppercase tracking-widest">{date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</p>
                               </div>
                            </div>
                          );
                        })
                    )}
                 </div>
              </div>

              <div className="bg-gradient-to-br from-blue-950 to-indigo-900 p-10 rounded-[3.5rem] text-white shadow-4xl relative overflow-hidden group">
                 <div className="absolute top-0 right-0 p-8 opacity-10 text-[120px] pointer-events-none group-hover:rotate-12 transition-transform duration-1000"><i className="fas fa-clock"></i></div>
                 <div className="relative z-10">
                    <p className="text-[10px] font-black uppercase tracking-[0.3em] text-blue-300 mb-4">Availability Engine</p>
                    <h4 className="text-2xl font-black mb-6 tracking-tight leading-tight">Optimizing your sync capacity.</h4>
                    <div className="space-y-4">
                       <div className="flex justify-between items-center text-[10px] font-black uppercase tracking-widest opacity-60">
                          <span>Weekly Utilization</span>
                          <span>{((scheduled.filter(s => {
                            const d = new Date(s.scheduledTime!);
                            const now = new Date();
                            const diff = Math.abs(now.getTime() - d.getTime());
                            return diff < 604800000;
                          }).length / (dbService.getProfessionalProfile(user.id)?.maxMeetingsPerWeek || 3)) * 100).toFixed(0)}%</span>
                       </div>
                       <div className="w-full h-1 bg-white/10 rounded-full overflow-hidden">
                          <div className="h-full bg-blue-400 rounded-full" style={{ width: '45%' }}></div>
                       </div>
                    </div>
                 </div>
              </div>
           </div>
        </div>
    </div>
  );
};

export default CalendarView;
